/*
  Journey Editor Redux Slice

  This module defines the Redux slice for the journey editor, including
  reducers and action creators for managing the document model state.
*/

import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  JourneyDocument,
  JourneyWorkflowMetadata,
  ApiJourneyVersionData,
  Node,
  Edge,
  NodeId,
} from '../types';
import type { ExpansionBounds, JourneyEditorProps } from './state';
import { initialState } from './state';
import { deriveJourneyWorkflows } from './helpers';
import {
  addNodeToDocument,
  updateNodeInDocument,
  deleteNodesFromDocument,
  addEdgeToDocument,
  updateEdgeInDocument,
  deleteEdgesFromDocument,
  connectNodes as connectNodesHelper,
  reconnectEdge as reconnectEdgeHelper,
  updateBranchCondition as updateBranchConditionHelper,
  addNodeWithOutcomes as addNodeWithOutcomesHelper,
  addReviewNodeWithOutcomes as addReviewNodeWithOutcomesHelper,
} from './mutations';
import { saveJourneyVersion } from './saveThunk';

/**
 * Journey Editor Redux Slice
 */
export const journeyEditorSlice = createSlice({
  name: 'journeyEditor',
  initialState,
  reducers: {
    /**
     * Clear all state and reset to initial values
     */
    clearState: () => initialState,

    /**
     * Set loading status
     */
    setLoading: state => {
      state.status = 'loading';
      state.error = null;
    },

    /**
     * Set error message
     */
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.status = 'failed';
    },

    /**
     * Set document and API snapshot (used by hydration thunk)
     */
    setDocumentAndSnapshot: (
      state,
      action: PayloadAction<{ doc: JourneyDocument; apiData: ApiJourneyVersionData }>,
    ) => {
      state.status = 'succeeded';
      state.doc = action.payload.doc;
      state.lastApiSnapshot = action.payload.apiData;
      state.error = null;
      state.hasUnsavedChanges = false;
      state.unified.workflows = deriveJourneyWorkflows(
        action.payload.doc,
        state.unified.workflows,
      );
    },

    /**
     * Update the document model directly
     */
    updateDocument: (state, action: PayloadAction<JourneyDocument>) => {
      state.status = 'succeeded';
      state.doc = action.payload;
      state.error = null;
      state.unified.workflows = deriveJourneyWorkflows(action.payload, state.unified.workflows);
    },

    /**
     * Set metadata for workflows referenced in the journey document
     */
    setJourneyWorkflows: (
      state,
      action: PayloadAction<Record<string, JourneyWorkflowMetadata>>,
    ) => {
      state.unified.workflows = action.payload;
    },

    /**
     * Set journey editor props (reference data for workflows, actions, outcomes)
     */
    setProps: (state, action: PayloadAction<JourneyEditorProps>) => {
      state.props = action.payload;
    },

    /**
     * Set the active editor context (journey or workflow token)
     */
    setActiveEditor: (state, action: PayloadAction<'journey' | string>) => {
      state.unified.activeEditor = action.payload;
    },

    /**
     * Track the currently focused journey node
     */
    setFocusedJourneyNode: (state, action: PayloadAction<string | null>) => {
      state.unified.focusedJourneyNodeId = action.payload;
    },

    /**
     * Update stored expansion bounds for a workflow container
     */
    setWorkflowExpansionBounds: (
      state,
      action: PayloadAction<{
        workflowToken: string;
        bounds: ExpansionBounds;
      }>,
    ) => {
      const { workflowToken, bounds } = action.payload;
      state.unified.expansionBounds[workflowToken] = bounds;
    },

    /**
     * Expand a workflow (enforcing single expansion)
     */
    expandWorkflow: (
      state,
      action: PayloadAction<{ workflowToken: string; bounds: ExpansionBounds }>,
    ) => {
      state.unified.expandedWorkflowToken = action.payload.workflowToken;
      state.unified.expansionBounds[action.payload.workflowToken] = action.payload.bounds;
      state.unified.activeEditor = action.payload.workflowToken;
    },

    /**
     * Collapse the currently expanded workflow
     */
    collapseWorkflow: state => {
      state.unified.expandedWorkflowToken = null;
      state.unified.activeEditor = 'journey';
    },

    /**
     * Add a node to the document
     */
    addNode: (state, action: PayloadAction<Node>) => {
      if (!state.doc) return;
      state.doc = addNodeToDocument(state.doc, action.payload);
      state.hasUnsavedChanges = true;
    },

    /**
     * Update a node in the document
     */
    updateNode: (
      state,
      action: PayloadAction<{ nodeId: NodeId; updates: Partial<Node> }>,
    ) => {
      if (!state.doc) return;
      state.doc = updateNodeInDocument(state.doc, action.payload.nodeId, action.payload.updates);
      state.hasUnsavedChanges = true;
    },

    /**
     * Delete nodes from the document (with cascade deletion)
     */
    deleteNodes: (
      state,
      action: PayloadAction<{ nodeIds: NodeId[]; cascade?: boolean }>,
    ) => {
      if (!state.doc) return;
      state.doc = deleteNodesFromDocument(
        state.doc,
        action.payload.nodeIds,
        action.payload.cascade ?? true,
      );
      state.hasUnsavedChanges = true;
    },

    /**
     * Add an edge to the document
     */
    addEdge: (state, action: PayloadAction<Edge>) => {
      if (!state.doc) return;
      state.doc = addEdgeToDocument(state.doc, action.payload);
      state.hasUnsavedChanges = true;
    },

    /**
     * Update an edge in the document
     */
    updateEdge: (
      state,
      action: PayloadAction<{ edgeId: string; updates: Partial<Edge> }>,
    ) => {
      if (!state.doc) return;
      state.doc = updateEdgeInDocument(state.doc, action.payload.edgeId, action.payload.updates);
      state.hasUnsavedChanges = true;
    },

    /**
     * Delete edges from the document
     */
    deleteEdges: (state, action: PayloadAction<string[]>) => {
      if (!state.doc) return;
      state.doc = deleteEdgesFromDocument(state.doc, action.payload);
      state.hasUnsavedChanges = true;
    },

    /**
     * Connect two nodes with an edge
     */
    connectNodes: (
      state,
      action: PayloadAction<{
        sourceNodeId: NodeId;
        targetNodeId: NodeId;
        edgeData?: Partial<Edge>;
      }>,
    ) => {
      if (!state.doc) return;
      const { sourceNodeId, targetNodeId, edgeData } = action.payload;
      state.doc = connectNodesHelper(state.doc, sourceNodeId, targetNodeId, edgeData);
      state.hasUnsavedChanges = true;
    },

    /**
     * Reconnect an edge to a new target
     */
    reconnectEdge: (
      state,
      action: PayloadAction<{ edgeId: string; newTargetNodeId: NodeId }>,
    ) => {
      if (!state.doc) return;
      state.doc = reconnectEdgeHelper(
        state.doc,
        action.payload.edgeId,
        action.payload.newTargetNodeId,
      );
      state.hasUnsavedChanges = true;
    },

    /**
     * Update a branch condition (for start node edges)
     */
    updateBranchCondition: (
      state,
      action: PayloadAction<{ edgeId: string; newCondition: string }>,
    ) => {
      if (!state.doc) return;
      state.doc = updateBranchConditionHelper(
        state.doc,
        action.payload.edgeId,
        action.payload.newCondition,
      );
      state.hasUnsavedChanges = true;
    },

    /**
     * Add a node with automatic child edges for each outcome (generic)
     */
    addNodeWithOutcomes: (
      state,
      action: PayloadAction<{ node: Node; outcomes?: string[] }>,
    ) => {
      if (!state.doc) return;

      // Build outcome map from props if available
      const outcomeMap: Record<string, number | null> = {};
      if (state.props?.outcomes) {
        state.props.outcomes.forEach(outcome => {
          outcomeMap[outcome.outcome] = outcome.customer_outcome_id || null;
        });
      }

      state.doc = addNodeWithOutcomesHelper(
        state.doc,
        action.payload.node,
        action.payload.outcomes,
        outcomeMap,
      );
      state.hasUnsavedChanges = true;
    },

    /**
     * Add a review node with automatic child edges for outcomes
     */
    addReviewNodeWithOutcomes: (
      state,
      action: PayloadAction<{ reviewNode: Node; outcomes?: string[] }>,
    ) => {
      if (!state.doc) return;

      // Build outcome map from props if available
      const outcomeMap: Record<string, number | null> = {};
      if (state.props?.outcomes) {
        state.props.outcomes.forEach(outcome => {
          outcomeMap[outcome.outcome] = outcome.customer_outcome_id || null;
        });
      }

      state.doc = addReviewNodeWithOutcomesHelper(
        state.doc,
        action.payload.reviewNode,
        action.payload.outcomes,
        outcomeMap,
      );
      state.hasUnsavedChanges = true;
    },

    /**
     * Set the start node ID for the journey
     */
    setStartNodeId: (state, action: PayloadAction<NodeId>) => {
      if (!state.doc) return;
      state.doc.start_node_id = action.payload;
      state.hasUnsavedChanges = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveJourneyVersion.fulfilled, state => {
        // Clear unsaved changes flag after successful save
        state.hasUnsavedChanges = false;
      });
  },
});

// Export action creators
export const {
  clearState,
  setLoading,
  setError,
  setDocumentAndSnapshot,
  updateDocument,
  setJourneyWorkflows,
  setProps,
  setActiveEditor,
  setFocusedJourneyNode,
  setWorkflowExpansionBounds,
  expandWorkflow,
  collapseWorkflow,
  addNode,
  updateNode,
  deleteNodes,
  addEdge,
  updateEdge,
  deleteEdges,
  connectNodes,
  reconnectEdge,
  updateBranchCondition,
  addNodeWithOutcomes,
  addReviewNodeWithOutcomes,
  setStartNodeId,
} = journeyEditorSlice.actions;

// Export reducer as default
export default journeyEditorSlice.reducer;
